import type { Expense } from '../types/expense';
import type { UserRole } from '../types/user';

// FE-12 — presentación, aritmética y permisos de UI de los gastos, en un solo
// sitio. Las tres superficies que los muestran (la página de gastos, el
// expediente del viaje y el formulario) comparten estas reglas.

// ---------------------------------------------------------------------------
// El importe. TODO lo de esta sección existe porque `Money` es TEXTO.
//
// El `Number` de JavaScript es un float64: `0.1 + 0.2` no da `0.3`, y una
// pantalla que sume así produce un total que no cuadra con el del motor. El
// contrato lo declaró texto justamente para que el front no cayera en eso
// (EP-28-gastos.md §5). Aquí se trabaja en CENTAVOS ENTEROS y se formatea solo
// al final.
// ---------------------------------------------------------------------------

/** Máximo del patrón del contrato: `^[0-9]{1,12}(\.[0-9]{1,2})?$`. */
const MAX_DIGITOS_ENTEROS = 12;

/** Canónico (`"50000.00"`) → centavos como entero exacto. */
function aCentavos(amount: string): bigint {
  const [enteros, decimales = ''] = amount.split('.');
  return BigInt(enteros || '0') * 100n + BigInt(decimales.padEnd(2, '0').slice(0, 2) || '0');
}

/** Centavos → canónico con los dos decimales que el contrato siempre trae. */
function desdeCentavos(centavos: bigint): string {
  const negativo = centavos < 0n;
  const abs = negativo ? -centavos : centavos;
  const entero = abs / 100n;
  const resto = abs % 100n;
  return `${negativo ? '-' : ''}${entero}.${String(resto).padStart(2, '0')}`;
}

/**
 * Convierte lo que una PERSONA escribe en el `Money` canónico del contrato, o
 * `null` si no se puede.
 *
 * En Colombia se escribe `50.000` con punto de miles, y ese valor **no pasa el
 * patrón del contrato**: mandarlo tal cual devuelve 400. Las reglas:
 *
 * - Con los dos separadores (`1.234,50`), el ÚLTIMO es el decimal.
 * - Con uno solo, decide cuántos dígitos lo siguen: **exactamente tres** →
 *   miles (`50.000` = cincuenta mil); **uno o dos** → decimales (`50,5` =
 *   cincuenta con cincuenta). Es la lectura que hace cualquiera mirando la
 *   cifra, y la única que no inventa dos órdenes de magnitud.
 * - Repetido (`1.234.567`) → siempre miles.
 * - **Lo que queda como miles tiene que estar bien agrupado.** `1.2345` y
 *   `1.234.56` no son ninguna de las dos lecturas: devuelven `null`. Antes se
 *   tragaban como miles y salían `12.345` y `123.456` —cien veces lo escrito—
 *   sin un aviso, porque la cifra resultante sí pasa el patrón `Money` y el
 *   servidor la acepta encantado.
 *
 * Devuelve `null` también para cero y negativos: un gasto de cero pesos no es
 * un gasto, y el contrato no admite el signo.
 */
export function parseMontoCOP(texto: string): string | null {
  // `\s` ya cubre el espacio duro (`\u00a0`), que es el que mete de su cuenta
  // un `Intl.NumberFormat` si alguien pega un importe ya formateado.
  const limpio = texto.replace(/[\s$]/g, '').trim();
  if (!limpio || !/^[\d.,]+$/.test(limpio)) return null;

  const puntos = (limpio.match(/\./g) ?? []).length;
  const comas = (limpio.match(/,/g) ?? []).length;

  let decimalEn = -1;
  if (puntos > 0 && comas > 0) {
    decimalEn = Math.max(limpio.lastIndexOf('.'), limpio.lastIndexOf(','));
  } else if (puntos === 1 || comas === 1) {
    const pos = puntos === 1 ? limpio.indexOf('.') : limpio.indexOf(',');
    const siguen = limpio.length - pos - 1;
    if (siguen === 1 || siguen === 2) decimalEn = pos;
  }

  const enterosCrudos = decimalEn === -1 ? limpio : limpio.slice(0, decimalEn);
  const enteros = enterosCrudos.replace(/[.,]/g, '');
  const decimales = decimalEn === -1 ? '' : limpio.slice(decimalEn + 1);

  // Si quedan separadores en la parte entera son de MILES, y los miles se
  // agrupan de tres en tres. Sin esta comprobación, cualquier cosa que no
  // encajara en las reglas de arriba caía en "todo son miles" y se aceptaba en
  // silencio con el orden de magnitud cambiado.
  if (/[.,]/.test(enterosCrudos) && !/^\d{1,3}([.,]\d{3})*$/.test(enterosCrudos)) return null;

  if (!/^\d+$/.test(enteros)) return null;
  if (decimales && !/^\d{1,2}$/.test(decimales)) return null;

  const sinCeros = enteros.replace(/^0+(?=\d)/, '');
  if (sinCeros.length > MAX_DIGITOS_ENTEROS) return null;

  const canonico = `${sinCeros}.${decimales.padEnd(2, '0')}`;
  if (aCentavos(canonico) <= 0n) return null;
  return canonico;
}

/**
 * Suma una lista de `Money`. En centavos enteros con `bigint`, nunca con
 * `Number`: sumar cien importes de doce dígitos con float64 pierde centavos, y
 * esta cifra se enseña junto a los gastos que la componen.
 */
export function sumarMontos(amounts: string[]): string {
  return desdeCentavos(amounts.reduce((total, a) => total + aCentavos(a), 0n));
}

const FORMATO_COP = new Intl.NumberFormat('es-CO', {
  style: 'currency',
  currency: 'COP',
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

/**
 * `"50000.00"` → `"$ 50.000"`. Los centavos se muestran solo si los hay: el
 * peso colombiano no los usa en la práctica, y pintar `,00` en cada fila añade
 * ruido a una columna que se lee de un vistazo.
 *
 * Formatea sobre `Number` **a propósito y solo aquí**: es la última operación,
 * ya no se suma nada después, y un importe suelto de doce dígitos cabe exacto
 * en un float64. Lo que no cabe es la SUMA — por eso `sumarMontos` no pasa por
 * aquí hasta el final.
 */
export function formatearCOP(amount: string): string {
  const n = Number(amount);
  if (Number.isNaN(n)) return amount;
  return FORMATO_COP.format(n);
}

// ---------------------------------------------------------------------------
// Presentación
// ---------------------------------------------------------------------------

/**
 * Color por CÓDIGO de categoría, nunca por id ni por etiqueta.
 *
 * `code` es el único eje estable del catálogo: el `id` es un UUID distinto en
 * cada tenant y la `label` la puede renombrar un Administrador esta tarde. Los
 * nueve de abajo son la semilla de ADR-015; una categoría que el tenant invente
 * cae al gris neutro **a propósito** — inventarle color a una taxonomía que no
 * conocemos es pintar significado donde no lo hay.
 */
const CATEGORIA_COLORES: Record<string, string> = {
  COMBUSTIBLE: '#F59E0B',
  PEAJES: '#0EA5E9',
  MANTENIMIENTO: '#8B5CF6',
  VIATICOS: '#14B8A6',
  ALIMENTACION: '#22C55E',
  HOSPEDAJE: '#6366F1',
  PARQUEADERO: '#64748B',
  MULTAS: '#EF4444',
  OTROS: '#6B7280',
};

const COLOR_NEUTRO = '#6B7280';

export function colorDeCategoria(code: string): string {
  return CATEGORIA_COLORES[code] ?? COLOR_NEUTRO;
}

/** Vehículo, conductor o viaje: de qué cuelga el gasto, en texto. */
export function asociacionTexto(e: Expense): string {
  const partes: string[] = [];
  if (e.vehiclePlate) partes.push(e.vehiclePlate);
  else if (e.vehicleId) partes.push('vehículo');
  if (e.driverName) partes.push(e.driverName);
  else if (e.driverId) partes.push('conductor');
  if (e.tripId) partes.push('viaje asignado');
  return partes.length > 0 ? partes.join(' · ') : 'sin asociación';
}

/** Un gasto sin soporte contable. Con el recibo opcional, es el dato que dice a quién hay que ir a buscar. */
export function sinRecibo(e: Expense): boolean {
  return !e.receipt;
}

/**
 * `true` si el gasto se corrigió alguna vez. No produce falsos positivos: el
 * backend **no escribe** `updatedAt` cuando una corrección no cambia nada —
 * devuelve 200 y no anota. Se compara con un segundo de tolerancia porque
 * `createdAt` y `updatedAt` los pone el mismo INSERT y nada garantiza que la
 * representación textual sea idéntica al milisegundo.
 */
export function fueCorregido(e: Expense): boolean {
  if (!e.updatedAt) return false;
  return Math.abs(Date.parse(e.updatedAt) - Date.parse(e.createdAt)) > 1000;
}

// `formatearPeso` se movió a `utils/format.ts` en FE-30: formatear bytes no es
// del dominio de gastos, y dejarla aquí obligaba a novedades a importar de
// gastos. Se re-exporta para no tocar a quien ya la importaba de este módulo.
export { formatearPeso } from './format';

// ---------------------------------------------------------------------------
// Permisos de UI — PRODUCT.md §4.8, verificado contra
// backend/services/core/internal/console/scope.go:182-188 (la matriz real).
//
// ⚠️ Esto decide qué BOTÓN se ve, nunca si la operación es legítima: el
// backend responde 403/404 y es él quien manda (contrato §7).
// ---------------------------------------------------------------------------

/** Administrador, Supervisor y Operador registran. El Ejecutivo mira y no toca. */
export function puedeRegistrar(role: UserRole | undefined): boolean {
  return role === 'admin' || role === 'supervisor' || role === 'operator';
}

/**
 * Mismo reparto que registrar. El "solo los propios" del Operador NO lo decide
 * este permiso sino su visibilidad: un gasto ajeno le responde 404, así que ni
 * siquiera lo tiene en pantalla para intentar corregirlo.
 */
export function puedeCorregir(role: UserRole | undefined): boolean {
  return puedeRegistrar(role);
}

/** Ventana de edición del Operador — issue #150, decisión revisada 2026-09-17. */
const VENTANA_EDICION_OPERADOR_DIAS = 14;

/**
 * Además del rol (`puedeCorregir`), el Operador pierde la edición 14 días
 * después de registrar el gasto (reunión Mindor Dev Team, issue #150).
 * Admin/Supervisor no tienen esta ventana — ya pueden corregir con auditoría
 * sin plazo (`PRODUCT.md` §4.8).
 *
 * ⚠️ Es una restricción de UI, no de seguridad real: `PATCH /expenses/{id}`
 * hoy solo declara `403` por rol, nunca por tiempo transcurrido — el
 * enforcement del lado del servidor es un hallazgo de contrato escalado
 * aparte (ver #158). Un Operador que llame la API directo puede corregir
 * igual pasada la ventana hasta que el backend lo aplique.
 */
export function dentroDeVentanaEdicion(e: Expense, role: UserRole | undefined): boolean {
  if (role !== 'operator') return true;
  const limite = Date.now() - VENTANA_EDICION_OPERADOR_DIAS * 24 * 60 * 60 * 1000;
  return Date.parse(e.createdAt) >= limite;
}

/** Subir un recibo exige `ActionRegisterExpense`: la misma terna que registrar. */
export function puedeSubirRecibo(role: UserRole | undefined): boolean {
  return puedeRegistrar(role);
}

/** La taxonomía es del tenant entero: solo el Administrador la toca. */
export function puedeGestionarCatalogo(role: UserRole | undefined): boolean {
  return role === 'admin';
}

/**
 * Traduce un `ApiError` de gastos a algo que una persona pueda leer.
 *
 * El `404` es el caso que más confunde si se cuenta mal: para el Operador que
 * pide un gasto ajeno, el servidor responde 404 —no 403— a propósito, para no
 * confirmarle que existe. "No existe o está fuera de tu alcance" es la
 * traducción honesta; decir "no tienes permiso" revelaría lo que el backend se
 * guarda.
 *
 * El tope del recibo llega como **400**, no como el 413 que declara la spec: el
 * core lo devuelve por `ErrInvalidInput`. Se traducen los dos.
 */
export function mensajeErrorGasto(
  err: unknown,
  accion: 'registrar' | 'corregir' | 'recibo' | 'catalogo',
): string {
  const status = (err as { status?: number })?.status;
  const detail = (err as { detail?: string })?.detail;

  if (status === 403) {
    if (accion === 'catalogo') return 'Solo un Administrador puede cambiar el catálogo de categorías.';
    return 'No tienes permiso para registrar ni corregir gastos.';
  }
  if (status === 404) return 'El gasto, el vehículo, el conductor o el viaje no existe o está fuera de tu alcance.';
  if (status === 409) return detail ?? 'Ya existe una categoría con ese código.';
  if (status === 413) return 'El recibo supera el tope de 10 MB.';
  if (status === 501) return 'El almacén de recibos no está configurado en este entorno.';
  if (status === 400) {
    if (accion === 'recibo') {
      return detail ?? 'El recibo tiene que ser una imagen (JPG, PNG, HEIC, WEBP) o un PDF, y pesar menos de 10 MB.';
    }
    return detail ?? 'Revisa los datos: el servidor los rechazó.';
  }
  return detail ?? 'No se pudo completar la operación. Inténtalo de nuevo.';
}

// ---------------------------------------------------------------------------
// Rango de consulta del listado.
//
// ⚠️ Filtra por `incurredAt` — CUÁNDO SE INCURRIÓ el gasto, no cuándo se
// digitó. Un peaje de septiembre registrado en octubre sale en septiembre.
//
// El valor por defecto es "Este mes", no "Todos": los gastos crecen mucho más
// rápido que los viajes (cada tanqueo, cada peaje) y esta pantalla trae el
// listado completo sin paginar.
// ---------------------------------------------------------------------------

export type ExpenseDateFilter = 'today' | '7days' | 'month' | 'all';

export const EXPENSE_DATE_FILTERS: { id: ExpenseDateFilter; label: string }[] = [
  { id: 'today', label: 'Hoy' },
  { id: '7days', label: 'Últimos 7 días' },
  { id: 'month', label: 'Este mes' },
  { id: 'all', label: 'Todos' },
];

export function rangoDeFiltro(filtro: ExpenseDateFilter): { from?: string; to?: string } {
  if (filtro === 'all') return {};
  const inicio = new Date();
  const fin = new Date();
  fin.setHours(23, 59, 59, 999);

  if (filtro === 'today') {
    inicio.setHours(0, 0, 0, 0);
  } else if (filtro === '7days') {
    inicio.setDate(inicio.getDate() - 6);
    inicio.setHours(0, 0, 0, 0);
  } else {
    // El mes ENTERO, no hasta hoy: nada impide registrar un gasto con fecha del
    // final del mes, y cortar el rango en hoy lo escondería.
    inicio.setDate(1);
    inicio.setHours(0, 0, 0, 0);
    fin.setMonth(fin.getMonth() + 1, 0);
    fin.setHours(23, 59, 59, 999);
  }

  return { from: inicio.toISOString(), to: fin.toISOString() };
}
